import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Radio,
  RadioGroup,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Tabs,
  Tab,
} from '@mui/material';
import { Check, Eye, FileImage, Pencil, Plus, Tag, Trash2, X } from 'lucide-react';
import { customersApi } from '../../../api/customers.api';
import { liveChatApi } from '../../../api/liveChat.api';
import useUIStore from '../../../store/useUIStore';
import useConfirm from '../../../hooks/useConfirm';
import { useNavigate } from 'react-router-dom';

const CustomersPage = ({
  sess = { uid: '', role: 'user' },
}) => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useUIStore();
  const { confirm } = useConfirm();
  const [startingChatId, setStartingChatId] = useState(null);
  const [allData, setAllData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showTagModal, setShowTagModal] = useState(false);
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [showCustomerModal, setShowCustomerModal] = useState(false);
  const [tab, setTab] = useState('customers');
  const [tags, setTags] = useState([]);
  const [tagForm, setTagForm] = useState({ name: '' });
  const [editingTagId, setEditingTagId] = useState(null);
  const [tagLoading, setTagLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState({});
  const [tagDetails, setTagDetails] = useState({});
  const [formData, setFormData] = useState({});
  const [selectedFile, setSelectedFile] = useState(null);
  const [showAddTagModal, setShowAddTagModal] = useState(false);
  const [selectedTagToAdd, setSelectedTagToAdd] = useState('');
  const [showCustomerTagsModal, setShowCustomerTagsModal] = useState(false);
  const [customerTags, setCustomerTags] = useState([]);
  const [showCustomersByTagModal, setShowCustomersByTagModal] = useState(false);
  const [selectedTagForView, setSelectedTagForView] = useState(null);
  const [customersByTag, setCustomersByTag] = useState([]);

  const fileInputRef = useRef(null);

  const rowsPerPage = 10;

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data;
    return (
      data?.error?.error_user_msg ||
      data?.error?.message ||
      data?.message ||
      data?.error ||
      error?.message ||
      fallback
    );
  };

  useEffect(() => {
    getDetails();
    fetchTags();
  }, []);

  useEffect(() => {
    if (tab === 'tags') {
      fetchTags();
    }
  }, [tab]);

  const getDetails = async () => {
    try {
      const response = await customersApi.getAll({ page: 1, limit: 100 });
      const payload = response?.data || {};
      const list = Array.isArray(payload?.data)
        ? payload.data
        : Array.isArray(payload?.customers)
          ? payload.customers
          : Array.isArray(payload)
            ? payload
            : [];
      setAllData(list);
      setFilteredData(list);
    } catch (error) {
      console.error(error);
      showError(getApiErrorMessage(error, 'Failed to load customers'));
      setAllData([]);
      setFilteredData([]);
    }
  };

  const fetchTags = async () => {
    try {
      setTagLoading(true);
      const response = await customersApi.getTags();
      const list =
        response?.data?.data ||
        response?.data?.tags ||
        response?.data ||
        [];
      setTags(Array.isArray(list) ? list : []);
    } catch (error) {
      console.error('Failed to load tags', error);
      showError(getApiErrorMessage(error, 'Failed to load tags'));
      setTags([]);
    } finally {
      setTagLoading(false);
    }
  };

  const handleTagSave = async () => {
    if (!tagForm.name.trim()) return;
    try {
      if (editingTagId) {
        await customersApi.updateTag(editingTagId, { name: tagForm.name.trim() });
      } else {
        await customersApi.createTag({ name: tagForm.name.trim() });
      }
      setTagForm({ name: '' });
      setEditingTagId(null);
      fetchTags();
    } catch (error) {
      console.error('Failed to save tag', error);
      showError(getApiErrorMessage(error, 'Failed to save tag'));
    }
  };

  const handleTagEdit = (tag) => {
    setEditingTagId(tag.tag_id || tag.id || tag._id || tag.tagId);
    setTagForm({ name: tag.tag_name || tag.name || tag.tag || '' });
  };

  const handleTagDelete = async (tag) => {
    const id = tag.tag_id || tag.id || tag._id || tag.tagId;
    if (!id) return;
    const isConfirmed = await confirm({
      title: 'Delete Tag',
      message: 'Delete this tag?',
      confirmText: 'Delete',
      confirmColor: 'error',
      severity: 'error',
    });
    if (!isConfirmed) return;
    try {
      await customersApi.deleteTag(id);
      fetchTags();
    } catch (error) {
      console.error('Failed to delete tag', error);
      showError(getApiErrorMessage(error, 'Failed to delete tag'));
    }
  };

  const handleViewCustomersByTag = (tag) => {
    const tagName = tag.tag_name || tag.name || tag.tag || '';
    setSelectedTagForView(tagName);
    // Filter customers who have this tag
    const filtered = allData.filter((customer) => {
      const rawTags = customer.tags || customer.cd_tags || [];
      const custTagNames = rawTags.map((t) =>
        typeof t === 'string' ? t : t.tag_name || t.name || t.tag || ''
      );
      return custTagNames.includes(tagName);
    });
    setCustomersByTag(filtered);
    setShowCustomersByTagModal(true);
  };

  const closeCustomersByTagModal = () => {
    setShowCustomersByTagModal(false);
    setSelectedTagForView(null);
    setCustomersByTag([]);
  };

  const handleRemoveTagFromCustomerInView = async (customerId) => {
    if (!customerId || !selectedTagForView) return;
    try {
      await customersApi.removeTag(customerId, selectedTagForView);
      // Remove customer from the list
      setCustomersByTag((prev) => prev.filter((c) => c.cd_id !== customerId));
      getDetails();
    } catch (error) {
      console.error('Failed to remove tag from customer', error);
      showError(getApiErrorMessage(error, 'Failed to remove tag from customer'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date
      .toLocaleString('en-US', {
        weekday: 'short',
        year: 'numeric',
        month: 'short',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        timeZone: 'Asia/Kolkata',
      })
      .replace(/,/g, '');
  };

  const openBulkUploadModal = () => {
    setShowBulkUploadModal(true);
  };

  const closeBulkUploadModal = () => {
    setShowBulkUploadModal(false);
    setSelectedFile(null);
  };

  const openEditModal = async (uid, cid) => {
    try {
      const response = await customersApi.getDetails(cid);
      const data = response?.data?.data || response?.data || {};
      setFormData({
        u_id: uid,
        cid: cid,
        age: data.cs_age || '',
        gender: data.cs_gender || '',
        location: data.cs_location || '',
        language: data.cs_language || '',
        interactionFrequency: data.cs_interaction_frequency || '',
        interest: data.cs_interest || '',
        productPreference: data.cs_product_preference || '',
        purchaseHistory: data.cs_purchase_history || '',
        customerSatisfaction: data.cs_satisfaction || '',
        engagementLevel: data.cs_engagement_level || '',
        customerLifetimeValue: data.cs_lifetime || '',
        rfm: data.cs_rfm || '',
      });
      setShowEditModal(true);
    } catch (error) {
      console.error('Error fetching user details:', error);
      // Still open the modal with empty form for new entry
      setFormData({ u_id: uid, cid: cid });
      setShowEditModal(true);
    }
  };

  const closeEditModal = () => {
    setShowEditModal(false);
    setFormData({});
  };

  const openTagModal = async (uid, cnumber) => {
    try {
      const response = await customersApi.getInformation(cnumber);
      const data = response?.data?.data || response?.data || {};
      if (data && Object.keys(data).length > 0) {
        setTagDetails(data);
        setSelectedCustomer({ uid, cnumber });
        setShowTagModal(true);
      } else {
        showError('No tag details available for this customer');
      }
    } catch (error) {
      console.error('Error fetching tag details:', error);
      showError(getApiErrorMessage(error, 'Error fetching tag details'));
    }
  };

  const closeTagModal = () => {
    setShowTagModal(false);
    setTagDetails({});
  };

  const openAddTagModal = async (customer) => {
    setSelectedCustomer(customer);
    setSelectedTagToAdd('');
    // Fetch tags if not already loaded
    if (tags.length === 0) {
      await fetchTags();
    }
    setShowAddTagModal(true);
  };

  const closeAddTagModal = () => {
    setShowAddTagModal(false);
    setSelectedCustomer({});
    setSelectedTagToAdd('');
  };

  const handleAddTagToCustomer = async () => {
    if (!selectedTagToAdd) return;
    const customerId = selectedCustomer.cd_id;
    if (!customerId) return;
    try {
      await customersApi.addTag(customerId, selectedTagToAdd);
      closeAddTagModal();
      getDetails();
    } catch (error) {
      console.error('Failed to add tag to customer', error);
      showError(getApiErrorMessage(error, 'Failed to add tag to customer'));
    }
  };

  const openCustomerTagsModal = async (customer) => {
    setSelectedCustomer(customer);
    // Extract tags from customer data
    const rawTags = customer.tags || customer.cd_tags || [];
    // Handle both array of strings and array of objects
    const custTags = (Array.isArray(rawTags) ? rawTags : []).map((t) =>
      typeof t === 'string' ? t : t.tag_name || t.name || t.tag || ''
    ).filter(Boolean);
    setCustomerTags(custTags);
    // Fetch available tags if not loaded
    if (tags.length === 0) {
      await fetchTags();
    }
    setShowCustomerTagsModal(true);
  };

  const closeCustomerTagsModal = () => {
    setShowCustomerTagsModal(false);
    setSelectedCustomer({});
    setCustomerTags([]);
  };

  const handleRemoveTagFromCustomer = async (tagName) => {
    const customerId = selectedCustomer.cd_id;
    if (!customerId || !tagName) return;
    try {
      await customersApi.removeTag(customerId, tagName);
      // Update local state
      setCustomerTags((prev) => prev.filter((t) => t !== tagName));
      getDetails();
    } catch (error) {
      console.error('Failed to remove tag from customer', error);
      showError(getApiErrorMessage(error, 'Failed to remove tag from customer'));
    }
  };

  const handleAddTagFromModal = async () => {
    if (!selectedTagToAdd) return;
    const customerId = selectedCustomer.cd_id;
    if (!customerId) return;
    try {
      await customersApi.addTag(customerId, selectedTagToAdd);
      // Update local state
      setCustomerTags((prev) => [...prev, selectedTagToAdd]);
      setSelectedTagToAdd('');
      getDetails();
    } catch (error) {
      console.error('Failed to add tag to customer', error);
      showError(getApiErrorMessage(error, 'Failed to add tag to customer'));
    }
  };

  const openCustomerModal = async (customerId) => {
    try {
      const response = await customersApi.getById(customerId);
      const data = response?.data?.data || response?.data || {};
      setSelectedCustomer({
        id: customerId,
        cd_number: data.cd_number || '',
        cd_name: data.cd_name || '',
        cd_email: data.cd_email || '',
        cd_address: data.cd_address || '',
        cd_city: data.cd_city || '',
        cd_state: data.cd_state || '',
        cd_country: data.cd_country || '',
        cd_pincode: data.cd_pincode || '',
      });
      setShowCustomerModal(true);
    } catch (error) {
      console.error('Error fetching customer details:', error);
      showError(getApiErrorMessage(error, 'Error fetching customer details'));
    }
  };

  const closeCustomerModal = () => {
    setShowCustomerModal(false);
    setSelectedCustomer({});
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setSelectedCustomer((prev) => ({ ...prev, [name]: value }));
  };

  const handleSegmentationSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await customersApi.update(formData.cid, {
        cs_age: formData.age,
        cs_gender: formData.gender,
        cs_location: formData.location,
        cs_language: formData.language,
        cs_interaction_frequency: formData.interactionFrequency,
        cs_interest: formData.interest,
        cs_product_preference: formData.productPreference,
        cs_purchase_history: formData.purchaseHistory,
        cs_satisfaction: formData.customerSatisfaction,
        cs_engagement_level: formData.engagementLevel,
        cs_lifetime: formData.customerLifetimeValue,
        cs_rfm: formData.rfm,
      });
      const payload = response?.data || {};
      if (payload?.success === false || payload?.error) {
        showError(payload?.message || payload?.error || 'Error updating details');
        return;
      }
      showSuccess('Details updated successfully');
      closeEditModal();
      getDetails();
    } catch (error) {
      console.error('Error updating details:', error);
      showError(getApiErrorMessage(error, 'Error updating details'));
    }
  };

  const handleCustomerUpdate = async (e) => {
    e.preventDefault();
    try {
      const { id, cd_id, ...payload } = selectedCustomer || {};
      const response = await customersApi.update(selectedCustomer.id, payload);
      const data = response?.data || {};
      if (data?.success === false || data?.error) {
        showError(data?.message || data?.error || 'Error updating customer details');
        return;
      }
      showSuccess('Customer details updated successfully!');
      closeCustomerModal();
      getDetails();
    } catch (error) {
      console.error('Error updating customer details:', error);
      showError(getApiErrorMessage(error, 'Error updating customer details'));
    }
  };

  const handleFileUpload = async (e) => {
    e.preventDefault();
    const file = selectedFile || fileInputRef.current?.files?.[0];

    if (!file) {
      showError('Please select a file');
      return;
    }

    const uploadFormData = new FormData();
    uploadFormData.append('file', file);

    try {
      const response = await customersApi.import(uploadFormData);
      const data = response?.data || {};
      if (data?.success === false || data?.error) {
        showError(data?.message || data?.error || 'File upload failed');
        return;
      }
      showSuccess(data?.message || 'File uploaded successfully');
      closeBulkUploadModal();
      getDetails();
    } catch (error) {
      console.error('File upload failed:', error);
      showError(getApiErrorMessage(error, 'File upload failed'));
    }
  };

  const handleToggleBlock = async (customer) => {
    if (!customer?.cd_id) return;
    const isActive = customer.cd_active === 0;
    const actionLabel = isActive ? 'block' : 'unblock';
    const isConfirmed = await confirm({
      title: `${actionLabel === 'block' ? 'Block' : 'Unblock'} Customer`,
      message: `Are you sure you want to ${actionLabel} this customer?`,
      confirmText: actionLabel === 'block' ? 'Block' : 'Unblock',
      confirmColor: actionLabel === 'block' ? 'error' : 'primary',
      severity: actionLabel === 'block' ? 'warning' : 'info',
    });
    if (!isConfirmed) return;
    try {
      if (isActive) {
        await customersApi.block(customer.cd_id);
      } else {
        await customersApi.unblock(customer.cd_id);
      }
      await getDetails();
    } catch (error) {
      console.error(`Failed to ${actionLabel} customer`, error);
      showError(getApiErrorMessage(error, `Failed to ${actionLabel} customer`));
    }
  };

  const handleStartLiveChat = async (customerId) => {
    if (!customerId) return;
    setStartingChatId(customerId);
    try {
      await liveChatApi.createSession({ cd_id: customerId, channel: 'whatsapp' });
      showSuccess('Live chat started');
      navigate('/chat');
    } catch (error) {
      showError(getApiErrorMessage(error, 'Failed to start live chat'));
    } finally {
      setStartingChatId(null);
    }
  };

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const currentData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} mb={2} alignItems="center">
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab label="Customers" value="customers" />
          <Tab label="Tags" value="tags" />
        </Tabs>
        {tab === 'customers' && (
          <Button variant="contained" onClick={openBulkUploadModal} sx={{ ml: 'auto' }}>
            Bulk Upload
          </Button>
        )}
      </Stack>

      {tab === 'customers' ? (
        <Card>
          <CardContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>S.No</TableCell>
                  <TableCell>Customers Name</TableCell>
                  <TableCell>Number</TableCell>
                  <TableCell>Country</TableCell>
                  <TableCell>Tags</TableCell>
                  <TableCell>Action</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7}>
                      <Typography align="center" color="text.secondary">
                        No data available
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  currentData.map((item, index) => {
                    const rawTags = item.tags || item.cd_tags || [];
                    // Handle both array of strings and array of objects
                    const itemTags = rawTags.map((t) =>
                      typeof t === 'string' ? t : t.tag_name || t.name || t.tag || ''
                    ).filter(Boolean);
                    return (
                      <TableRow key={item.cd_id} hover>
                        <TableCell>{startIndex + index + 1}</TableCell>
                        <TableCell title={item?.cd_name || '-'}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Typography>
                              {item?.cd_name && item.cd_name.trim()
                                ? item.cd_name
                                    .split(' ')
                                    .map((word) =>
                                      word.length > 30 ? `${word.substring(0, 30)}...` : word
                                    )
                                    .join(' ')
                                : '-'}
                            </Typography>
                            <IconButton
                              size="small"
                              color="primary"
                              aria-label="Edit customer"
                              onClick={() => openCustomerModal(item.cd_id)}
                            >
                              <Pencil size={16} />
                            </IconButton>
                          </Stack>
                        </TableCell>
                        <TableCell>{item.cd_number || '-'}</TableCell>
                        <TableCell>{item.cd_country || '-'}</TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ gap: 0.5 }}>
                            {itemTags.length > 0 ? (
                              <>
                                {itemTags.slice(0, 2).map((tagName, idx) => (
                                  <Chip
                                    key={idx}
                                    label={tagName}
                                    size="small"
                                    variant="outlined"
                                    color="primary"
                                  />
                                ))}
                                {itemTags.length > 2 && (
                                  <Chip
                                    label={`+${itemTags.length - 2}`}
                                    size="small"
                                    onClick={() => openCustomerTagsModal(item)}
                                    sx={{ cursor: 'pointer' }}
                                  />
                                )}
                              </>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                -
                              </Typography>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1}>
                            <IconButton
                              size="small"
                              color="success"
                              aria-label="Manage tags"
                              onClick={() => openCustomerTagsModal(item)}
                            >
                              <Tag size={16} />
                            </IconButton>
                            {Number(item.cd_active) === 1 && (
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => handleStartLiveChat(item.cd_id)}
                                disabled={startingChatId === item.cd_id}
                                sx={{ textTransform: 'none' }}
                              >
                                {startingChatId === item.cd_id ? 'Starting...' : 'Start Chat'}
                              </Button>
                            )}
                          </Stack>
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip
                              label={Number(item.cd_active) === 1 ? 'Active' : 'Inactive'}
                              color={Number(item.cd_active) === 1 ? 'success' : 'warning'}
                              size="small"
                              variant="outlined"
                            />
                          </Stack>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

            {totalPages > 1 && (
              <Stack direction="row" justifyContent="center" alignItems="center" spacing={2} mt={2}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  Previous
                </Button>
                <Typography variant="body2">Page {currentPage} of {totalPages}</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages}
                >
                  Next
                </Button>
              </Stack>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1}>
                <TextField
                  label={editingTagId ? 'Update tag name' : 'New tag name'}
                  value={tagForm.name}
                  onChange={(e) => setTagForm({ name: e.target.value })}
                  fullWidth
                />
                <Button variant="contained" onClick={handleTagSave}>
                  {editingTagId ? 'Update' : 'Add'}
                </Button>
                {editingTagId && (
                  <Button
                    variant="text"
                    onClick={() => {
                      setEditingTagId(null);
                      setTagForm({ name: '' });
                    }}
                  >
                    Cancel
                  </Button>
                )}
              </Stack>
              {tagLoading ? (
                <Typography color="text.secondary">Loading tags...</Typography>
              ) : tags.length === 0 ? (
                <Typography color="text.secondary">No tags available</Typography>
              ) : (
                <Stack spacing={1}>
                  {tags.map((tag) => {
                    const label = tag.tag_name || tag.name || tag.tag || 'Tag';
                    return (
                      <Stack
                        key={tag.tag_id || tag.id || tag._id || tag.tagId || label}
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{
                          p: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                        }}
                      >
                        <Typography variant="body2">{label}</Typography>
                        <Stack direction="row" spacing={1}>
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            startIcon={<Eye size={14} />}
                            onClick={() => handleViewCustomersByTag(tag)}
                          >
                            View Customers
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            onClick={() => handleTagEdit(tag)}
                          >
                            Edit
                          </Button>
                          <Button
                            size="small"
                            color="error"
                            variant="outlined"
                            onClick={() => handleTagDelete(tag)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </Stack>
                    );
                  })}
                </Stack>
              )}
            </Stack>
          </CardContent>
        </Card>
      )}

      <Dialog
        open={showBulkUploadModal}
        onClose={closeBulkUploadModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Bulk Customer Allocation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2">
              Create a CSV file with customer details, ensuring &quot;Customer Name&quot; and
              &quot;Customer Mobile Number with Country Code&quot; are filled out. Use the sample
              format before uploading.
            </Typography>
            <Button
              component="a"
              href="https://bot.chattybo.com/public/assets/Bulkuploadformat.csv"
              target="_blank"
              rel="noopener noreferrer"
              variant="outlined"
            >
              Download Sample CSV
            </Button>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button variant="contained" component="label" startIcon={<FileImage size={18} />}>
                Choose File
                <input
                  type="file"
                  hidden
                  accept=".csv"
                  ref={fileInputRef}
                  onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
                />
              </Button>
              {selectedFile && (
                <Stack direction="row" spacing={1} alignItems="center">
                  <Check size={16} color="green" />
                  <Typography variant="body2">{selectedFile.name}</Typography>
                </Stack>
              )}
            </Stack>
            <Typography variant="caption" color="text.secondary">
              Only .csv files allowed
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeBulkUploadModal}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleFileUpload}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showEditModal}
        onClose={closeEditModal}
        fullWidth
        maxWidth="lg"
      >
        <form onSubmit={handleSegmentationSubmit}>
          <DialogTitle>Add Customers Details</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12} sm={6}>
                <input type="hidden" name="u_id" value={formData.u_id || ''} />
                <input type="hidden" name="cid" value={formData.cid || ''} />
                <TextField
                  fullWidth
                  label="Age"
                  name="age"
                  value={formData.age || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
                <FormControl component="fieldset" margin="dense">
                  <FormLabel component="legend">Gender</FormLabel>
                  <RadioGroup
                    row
                    name="gender"
                    value={formData.gender || ''}
                    onChange={handleFormChange}
                  >
                    <FormControlLabel value="male" control={<Radio />} label="Male" />
                    <FormControlLabel value="female" control={<Radio />} label="Female" />
                    <FormControlLabel value="other" control={<Radio />} label="Other" />
                  </RadioGroup>
                </FormControl>
                <TextField
                  fullWidth
                  label="Location and Range"
                  name="location"
                  value={formData.location || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Language Preference"
                  name="language"
                  value={formData.language || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Interaction Frequency"
                  name="interactionFrequency"
                  value={formData.interactionFrequency || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Purchase History"
                  name="purchaseHistory"
                  value={formData.purchaseHistory || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Interest"
                  name="interest"
                  value={formData.interest || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Product Preference"
                  name="productPreference"
                  value={formData.productPreference || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Engagement Level"
                  name="engagementLevel"
                  value={formData.engagementLevel || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Customer Satisfaction"
                  name="customerSatisfaction"
                  value={formData.customerSatisfaction || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="Customer Lifetime Value (CLV)"
                  name="customerLifetimeValue"
                  value={formData.customerLifetimeValue || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
                <TextField
                  fullWidth
                  label="RFM (Recency, Frequency, Monetary)"
                  name="rfm"
                  value={formData.rfm || ''}
                  onChange={handleFormChange}
                  margin="dense"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closeEditModal}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Submit
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={showCustomerModal}
        onClose={closeCustomerModal}
        fullWidth
        maxWidth="md"
      >
        <form onSubmit={handleCustomerUpdate}>
          <DialogTitle>Add Basic Customer Details</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2} mt={0.5}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Number"
                  name="cd_number"
                  value={selectedCustomer.cd_number || ''}
                  onChange={handleCustomerChange}
                  margin="dense"
                  InputProps={{ readOnly: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Name"
                  name="cd_name"
                  value={selectedCustomer.cd_name || ''}
                  onChange={handleCustomerChange}
                  margin="dense"
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Customer Email"
                  name="cd_email"
                  value={selectedCustomer.cd_email || ''}
                  onChange={handleCustomerChange}
                  margin="dense"
                  type="email"
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Customer Address"
                  name="cd_address"
                  value={selectedCustomer.cd_address || ''}
                  onChange={handleCustomerChange}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="City"
                  name="cd_city"
                  value={selectedCustomer.cd_city || ''}
                  onChange={handleCustomerChange}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="State"
                  name="cd_state"
                  value={selectedCustomer.cd_state || ''}
                  onChange={handleCustomerChange}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Country"
                  name="cd_country"
                  value={selectedCustomer.cd_country || ''}
                  onChange={handleCustomerChange}
                  margin="dense"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="cd_pincode"
                  value={selectedCustomer.cd_pincode || ''}
                  onChange={handleCustomerChange}
                  margin="dense"
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" onClick={closeCustomerModal}>
              Cancel
            </Button>
            <Button type="submit" variant="contained">
              Update
            </Button>
          </DialogActions>
        </form>
      </Dialog>

      <Dialog
        open={showTagModal}
        onClose={closeTagModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Tag Details</DialogTitle>
        <DialogContent dividers>
          {Object.keys(tagDetails).length === 0 ? (
            <Typography color="text.secondary">No tag details available.</Typography>
          ) : (
            <Stack spacing={1}>
              {Object.entries(tagDetails).map(([key, value]) => (
                <Stack
                  key={key}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Typography variant="body2" color="text.secondary">
                    {key}
                  </Typography>
                  <Typography variant="body2">{value || '-'}</Typography>
                </Stack>
              ))}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeTagModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showAddTagModal}
        onClose={closeAddTagModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Add Tag to Customer</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} mt={1}>
            <Typography variant="body2" color="text.secondary">
              Select a tag to add to {selectedCustomer?.cd_name || 'this customer'}
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="add-tag-select-label">Select Tag</InputLabel>
              <Select
                labelId="add-tag-select-label"
                value={selectedTagToAdd}
                label="Select Tag"
                onChange={(e) => setSelectedTagToAdd(e.target.value)}
              >
                {tags.map((tag) => {
                  const tagName = tag.tag_name || tag.name || tag.tag || '';
                  const tagId = tag.tag_id || tag.id || tag._id || tag.tagId || tagName;
                  return (
                    <MenuItem key={tagId} value={tagName}>
                      {tagName}
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
            {tags.length === 0 && !tagLoading && (
              <Typography variant="body2" color="error">
                No tags available. Please create tags first in the Tags tab.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={closeAddTagModal}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleAddTagToCustomer}
            disabled={!selectedTagToAdd}
          >
            Add Tag
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCustomerTagsModal}
        onClose={closeCustomerTagsModal}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Manage Tags - {selectedCustomer?.cd_name || 'Customer'}
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
              Current Tags
            </Typography>
            {customerTags.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No tags assigned to this customer.
              </Typography>
            ) : (
              <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ gap: 1 }}>
                {customerTags.map((tag, idx) => {
                  // Handle both string and object tags
                  const tagName = typeof tag === 'string' ? tag : (tag?.tag_name || tag?.name || tag?.tag || '');
                  return (
                    <Chip
                      key={idx}
                      label={tagName}
                      color="primary"
                      variant="outlined"
                      onDelete={() => handleRemoveTagFromCustomer(tagName)}
                      deleteIcon={<X size={14} />}
                    />
                  );
                })}
              </Stack>
            )}

            <Typography variant="subtitle2" color="text.secondary" sx={{ mt: 2 }}>
              Add New Tag
            </Typography>
            <Stack direction="row" spacing={1}>
              <FormControl fullWidth size="small">
                <InputLabel id="manage-tag-select-label">Select Tag</InputLabel>
                <Select
                  labelId="manage-tag-select-label"
                  value={selectedTagToAdd}
                  label="Select Tag"
                  onChange={(e) => setSelectedTagToAdd(e.target.value)}
                >
                  {tags
                    .filter((tag) => {
                      const tagName = tag.tag_name || tag.name || tag.tag || '';
                      // Check if tag already exists (handle both string and object in customerTags)
                      const existingNames = customerTags.map((t) =>
                        typeof t === 'string' ? t : (t?.tag_name || t?.name || t?.tag || '')
                      );
                      return !existingNames.includes(tagName);
                    })
                    .map((tag) => {
                      const tagName = tag.tag_name || tag.name || tag.tag || '';
                      const tagId = tag.tag_id || tag.id || tag._id || tag.tagId || tagName;
                      return (
                        <MenuItem key={tagId} value={tagName}>
                          {tagName}
                        </MenuItem>
                      );
                    })}
                </Select>
              </FormControl>
              <Button
                variant="contained"
                onClick={handleAddTagFromModal}
                disabled={!selectedTagToAdd}
                sx={{ minWidth: 80 }}
              >
                Add
              </Button>
            </Stack>
            {tags.length === 0 && (
              <Typography variant="body2" color="error">
                No tags available. Please create tags first in the Tags tab.
              </Typography>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeCustomerTagsModal}>
            Done
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={showCustomersByTagModal}
        onClose={closeCustomersByTagModal}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          Customers with Tag: {selectedTagForView || ''}
        </DialogTitle>
        <DialogContent dividers>
          {customersByTag.length === 0 ? (
            <Typography color="text.secondary" align="center" py={3}>
              No customers found with this tag.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>S.No</TableCell>
                    <TableCell>Customer Name</TableCell>
                    <TableCell>Number</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {customersByTag.map((customer, index) => (
                    <TableRow key={customer.cd_id} hover>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{customer.cd_name || '-'}</TableCell>
                      <TableCell>{customer.cd_number || '-'}</TableCell>
                      <TableCell>{customer.cd_country || '-'}</TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="error"
                          aria-label="Remove tag"
                          onClick={() => handleRemoveTagFromCustomerInView(customer.cd_id)}
                        >
                          <Trash2 size={16} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <Typography variant="body2" color="text.secondary" mt={2}>
            Total: {customersByTag.length} customer(s)
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={closeCustomersByTagModal}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default CustomersPage;
